<!-- The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work. -->
<template>
    <div class="min-h-screen flex flex-col bg-white">
      <NavBar />
      <!-- 主内容区域 -->
      <div class="pt-24 pb-16 flex-1">
        <div class="container mx-auto px-6">
          <!-- 页面标题 -->
          <div class="mb-8 hidden md:block">
            <h1 class="text-3xl font-bold text-gray-800">作品商城</h1>
            <p class="text-gray-600 mt-2">发现独特创意，选购心仪作品</p>
          </div>
          <!-- 筛选和搜索区域 -->
          <div class="bg-white rounded-xl shadow-md p-6 mb-8">
            <div class="flex flex-row gap-2 md:flex-row md:gap-6 items-start md:items-center justify-between">
              <!-- 分类筛选 -->
              <div class="w-full md:w-auto">
                <div class="flex flex-wrap gap-2">
                  <button v-for="(category, index) in categories" :key="index" @click="toggleCategory(category)" :class="[
                    'px-3 py-1 rounded-full text-xs transition-colors duration-200 !rounded-button whitespace-nowrap cursor-pointer',
                    selectedCategory && selectedCategory.name === category.name ? 'bg-[#76B900] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  ]">
                    {{ category.name }}
                  </button>
                </div>
              </div>
              <!-- 排序选项 -->
              <div class="w-full md:w-auto hidden md:block">
                <div class="relative">
                  <button
                    @click="toggleSortDropdown"
                    class="flex items-center justify-between w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76B900] focus:border-transparent cursor-pointer whitespace-nowrap"
                  >
                    <span>{{ sortOptions[selectedSort].label }}</span>
                    <i class="fas fa-chevron-down ml-2 text-gray-500"></i>
                  </button>
                  <div
                    v-if="showSortDropdown"
                    class="absolute right-0 z-10 mt-1 w-48 bg-white border border-gray-300 rounded-lg shadow-lg py-2"
                  >
                    <div
                      v-for="(option, index) in sortOptions"
                      :key="index"
                      @click="selectSortOption(index)"
                      class="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <div class="flex items-center justify-between">
                        <span>{{ option.label }}</span>
                        <i v-if="selectedSort === index" class="fas fa-check text-[#76B900]"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 加载中 -->
          <div v-if="loading" class="text-center py-16">
            <i class="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
            <h3 class="text-xl font-semibold text-gray-700 mb-2">加载中...</h3>
          </div>
          <!-- 无数据 -->
          <div v-else-if="paginatedProducts.length === 0" class="text-center py-16">
            <i class="fas fa-search text-gray-300 text-5xl mb-4"></i>
            <h3 class="text-xl font-semibold text-gray-700 mb-2">未找到匹配的作品</h3>
            <p class="text-gray-500">请尝试更换搜索关键词或筛选条件</p>
          </div>
          <!-- 作品展示区域 -->
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            <ProductCard
              v-for="product in paginatedProducts"
              :key="product.id"
              :product="product"
            />
          </div>
          <!-- 分页导航 -->
          <div class="mt-12 flex flex-col sm:flex-row items-center justify-center">
            <div class="flex items-center">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="h-10 w-10 rounded-lg border border-gray-300 flex items-center justify-center mr-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer !rounded-button whitespace-nowrap"
              >
                <i class="fas fa-chevron-left text-gray-600"></i>
              </button>
              <div v-for="page in displayedPages" :key="page" class="mx-1">
                <button
                  v-if="page !== '...'"
                  @click="goToPage(page)"
                  :class="[
                    'h-10 w-10 rounded-lg flex items-center justify-center cursor-pointer !rounded-button whitespace-nowrap',
                    currentPage === page ? 'bg-[#76B900] text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  ]"
                >
                  {{ page }}
                </button>
                <span v-else class="h-10 w-10 flex items-center justify-center text-gray-600">...</span>
              </div>
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="h-10 w-10 rounded-lg border border-gray-300 flex items-center justify-center ml-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer !rounded-button whitespace-nowrap"
              >
                <i class="fas fa-chevron-right text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <FooterBar />
    </div>
  </template>
  
  <script>
  import NavBar from '@/components/common/NavBar.vue'
  import FooterBar from '@/components/common/FooterBar.vue'
  import ProductCard from '@/components/common/ProductCard.vue'
  import { getGoodsList, getGoodsStyleClass } from '@/api/product'
  
  export default {
    name: 'MallPage',
    components: { NavBar, FooterBar, ProductCard },
    data() {
      return {
        selectedCategory: null,
        showCategoryDropdown: false,
        showSortDropdown: false,
        selectedSort: 0,
        currentPage: 1,
        perPage: 20,
        selectAllCategories: false,
        isRefreshing: false,
        loading: false,
        products: [],
        total: 0,
        // 排序选项
        sortOptions: [
          { label: '综合排序', value: 'CompositeOrder' },
          { label: '最新发布', value: 'Latest' },
          { label: '价格从高到低', value: 'PriceDesc' },
          { label: '价格从低到高', value: 'PriceAsc' },
          // { label: '销量优先', value: 'SaleNumberPriority' },
          // { label: '收藏最多', value: 'CollectionsNumberMax' }
        ],
        // 分类列表
        categories: [],
      }
    },
    computed: {
      paginatedProducts() {
        return this.products;
      },
      totalPages() {
        return Math.ceil(this.total / this.perPage)
      },
      displayedPages() {
        const pages = []
        const maxPagesToShow = 5
        if (this.totalPages <= maxPagesToShow) {
          for (let i = 1; i <= this.totalPages; i++) {
            pages.push(i)
          }
        } else {
          const halfMaxPages = Math.floor(maxPagesToShow / 2)
          let startPage = Math.max(1, this.currentPage - halfMaxPages)
          let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1)
          if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1)
          }
          if (startPage > 1) {
            pages.push(1)
            if (startPage > 2) {
              pages.push('...')
            }
          }
          for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
          }
          if (endPage < this.totalPages) {
            if (endPage < this.totalPages - 1) {
              pages.push('...')
            }
            pages.push(this.totalPages)
          }
        }
        return pages
      }
    },
    methods: {
      async fetchProducts() {
        this.loading = true;
        this.products = [];
        try {
          const params = {
            order: this.sortOptions[this.selectedSort].value || 'CompositeOrder',
            page: this.currentPage,
            page_size: this.perPage
          };
          if (this.selectedCategory) {
            params.category_name = this.selectedCategory.name;
          }
          const res = await getGoodsList(params);
          const data = res || {};
          this.products = (data.items || []).map(item => ({
            id: item.id,
            title: item.guide_title,
            image: Array.isArray(item.main_image) ? item.main_image[0] : '',
            price: item.fixed_price,
            category: item.category_name,
            sold: item.sale_number,
            likes: item.collections_number,
          }));
          this.total = data.total || 0;
        } finally {
          this.loading = false;
        }
      },
      handleMallLinkClick() {
        if (!this.isRefreshing) {
          this.isRefreshing = true
          this.selectedCategory = null
          this.selectedSort = 0
          this.currentPage = 1
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
          setTimeout(() => {
            this.isRefreshing = false
          }, 800)
          this.fetchProducts()
        }
      },
      toggleCategoryDropdown() {
        this.showCategoryDropdown = !this.showCategoryDropdown
        if (this.showCategoryDropdown) {
          this.showSortDropdown = false
        }
      },
      toggleSortDropdown() {
        this.showSortDropdown = !this.showSortDropdown
        if (this.showSortDropdown) {
          this.showCategoryDropdown = false
        }
      },
      selectSortOption(index) {
        this.selectedSort = index
        this.showSortDropdown = false
        this.currentPage = 1
        this.fetchProducts()
      },
      clearAllCategories() {
        this.selectedCategory = null
        this.currentPage = 1
        this.fetchProducts()
      },
      updateSelectAllStatus() {
        this.selectAllCategories = this.selectedCategories.length === this.categories.length
      },
      goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
          this.currentPage = page
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
          this.fetchProducts()
        }
      },
      toggleCategory(category) {
        if (this.selectedCategory && this.selectedCategory.name === category.name) {
          this.selectedCategory = null;
        } else {
          this.selectedCategory = category;
        }
        this.currentPage = 1;
        this.fetchProducts();
      },
      async fetchCategories() {
        const res = await getGoodsStyleClass();
        this.categories = Array.isArray(res) ? res : [];
      },
    },
    watch: {
      perPage() {
        this.currentPage = 1
        this.fetchProducts()
      }
    },
    mounted() {
      document.addEventListener('click', (event) => {
        const target = event.target
        if (!target.closest('.relative') && (this.showCategoryDropdown || this.showSortDropdown)) {
          this.showCategoryDropdown = false
          this.showSortDropdown = false
        }
      })
      const mallLink = document.getElementById('mall-link')
      if (mallLink) {
        mallLink.addEventListener('click', (event) => {
          event.preventDefault()
          this.handleMallLinkClick()
        })
      }
      this.fetchCategories();
      this.fetchProducts();
    }
  }
  </script>
  
  <style scoped>
  /* 自定义滚动条样式 */
  .max-h-60::-webkit-scrollbar {
    width: 6px;
  }
  .max-h-60::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  .max-h-60::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
  .max-h-60::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
  /* 文本截断 */
  .line-clamp-1 {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* 禁用number输入框的箭头 */
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  </style>